import {
    ApplyFilter,
    FilterInputData,
    FilterListType,
    PLPManager,
    PLPState
} from '../FilterTypes'
import type { Maybe, Scalars } from '@pylot-data/pylotschema'
import { SearchCriteriaFilterActions } from '../hooks/use-plp-manager'

/**
 * Rebuild a new filter state and trigger product collection reload
 *
 * @param plpManager
 */
export const getApplyCriterionHandler = (
    plpManager: PLPManager
): ApplyFilter => {
    const { plpState, setSearchCriteria } = plpManager
    return (newCriterionData, isShouldApply = true, isSingleSelect = false) => {
        // Clear all criterion
        if (!newCriterionData) {
            setSearchCriteria([])
            return
        } else {
            let skipIndex = -1
            const matchCriterion = (
                {
                    attribute_code,
                    filter_value,
                    filter_action
                }: {
                    attribute_code: string
                    filter_value: string
                    filter_action: string
                },
                index: number
            ) => {
                let filterValue = filter_value

                if (
                    filter_action === SearchCriteriaFilterActions.FROM &&
                    plpState.appliedSearchCritieria[index + 1] &&
                    plpState.appliedSearchCritieria[index + 1].filter_action ===
                        SearchCriteriaFilterActions.TO
                ) {
                    filterValue = `${filter_value}_${
                        plpState.appliedSearchCritieria[index + 1].filter_value
                    }`
                    if (
                        attribute_code === newCriterionData.requestVar &&
                        filterValue === newCriterionData.value_string
                    ) {
                        skipIndex = index + 1
                    }
                }

                return (
                    skipIndex == index ||
                    (attribute_code === newCriterionData.requestVar &&
                        filterValue === newCriterionData.value_string)
                )
            }

            const indexIfCriterionExists =
                plpState.appliedSearchCritieria.findIndex(matchCriterion)

            const isCriterionApplied = indexIfCriterionExists > -1

            // Criterion is already in list so we don't need to reapply it,
            // or it's not in the list so we don't need to remove it.
            // This should never happen if the UI is correctly using search criteria functionality.
            if (
                (isCriterionApplied && isShouldApply) ||
                (!isCriterionApplied && !isShouldApply)
            )
                return
            // Criterion is already in list and we need to remove it
            else if (isCriterionApplied && !isShouldApply) {
                setSearchCriteria(
                    plpState.appliedSearchCritieria.filter(
                        (criterion, index) => !matchCriterion(criterion, index)
                    )
                )
            }
            // Criterion is not in the list and we need to add it
            else
                setSearchCriteria(
                    buildSearchCriteria(newCriterionData, plpState)
                )
        }
    }
}

const buildSearchCriteria = (
    newCriterionData: FilterInputData,
    plpState: PLPState
) => {
    if (newCriterionData.requestVar === 'price') {
        return [
            ...removeSearchCritieriaByAttribute(
                plpState,
                newCriterionData.requestVar
            ),
            ...buildPriceSearch(newCriterionData)
        ]
    } else {
        return [
            ...plpState.appliedSearchCritieria,
            {
                attribute_code: newCriterionData.requestVar,
                filter_action: SearchCriteriaFilterActions.EQ,
                filter_value: newCriterionData.value_string
            }
        ]
    }
}

const removeSearchCritieriaByAttribute = (
    plpState: PLPState,
    attributeCode: string
) =>
    plpState.appliedSearchCritieria.filter(
        (searchCriterion) => searchCriterion.attribute_code !== attributeCode
    )

const buildPriceSearch = (newCriterionData: FilterInputData) => {
    const priceRange = newCriterionData.value_string.split('_')
    const priceStart = priceRange[0]
    const priceEnd = priceRange[1]
    const attrCode = {
        attribute_code: newCriterionData.requestVar
    }
    if (priceStart === '*') {
        return [
            {
                ...attrCode,
                filter_action: SearchCriteriaFilterActions.FROM,
                filter_value: '0'
            },
            {
                ...attrCode,
                filter_action: SearchCriteriaFilterActions.TO,
                filter_value: priceEnd
            }
        ]
    } else if (priceEnd === '*') {
        return [
            {
                ...attrCode,
                filter_action: SearchCriteriaFilterActions.FROM,
                filter_value: priceStart
            },
            {
                ...attrCode,
                filter_action: SearchCriteriaFilterActions.TO,
                filter_value: ''
            }
        ]
    } else {
        return [
            {
                ...attrCode,
                filter_action: SearchCriteriaFilterActions.FROM,
                filter_value: priceStart
            },
            {
                ...attrCode,
                filter_action: SearchCriteriaFilterActions.TO,
                filter_value: priceEnd
            }
        ]
    }
}

/**
 * Manage the applied filters
 * return {}
 */
export const buildFilterInput = (
    filterInputData: FilterInputData,
    isApplied: boolean,
    state: PLPState
): FilterListType => {
    const { requestVar, value_string } = filterInputData

    if (!isApplied) {
        return deleteFilter(state, value_string, requestVar)
    }

    // 'eq' ensures that we filter only by one sub-category at a time
    if (requestVar === 'category_uid') {
        return {
            ...state.filters,
            ...{ [requestVar]: { ['eq']: value_string } }
        }
    }

    if (requestVar === 'price') {
        return buildPriceInput(state, value_string, requestVar)
    }

    if (requestVar in state.filters) {
        return buildAttributeInput(state, value_string, requestVar)
    }

    return {
        ...state.filters,
        [requestVar]: {
            in: [value_string]
        }
    }
}

const buildPriceInput = (
    state: PLPState,
    value_string: string,
    requestVar: string
): FilterListType => {
    const priceRange = value_string.split('_')
    const priceStart = priceRange[0]
    const priceEnd = priceRange[1]
    if (priceStart == '*') {
        return {
            ...state.filters,
            ...{ [requestVar]: { from: '0', to: priceEnd } }
        }
    } else if (priceEnd == '*') {
        return {
            ...state.filters,
            ...{ [requestVar]: { from: priceStart } }
        }
    } else if (!isNaN(parseFloat(priceStart)) && !isNaN(parseFloat(priceEnd))) {
        return {
            ...state.filters,
            ...{ [requestVar]: { from: priceStart, to: priceEnd } }
        }
    }
    return state.filters
}

const buildAttributeInput = (
    state: PLPState,
    value_string: string,
    requestVar: string
): FilterListType => {
    return {
        ...state.filters,
        ...{
            [requestVar]: {
                in: [...(state.filters[requestVar]['in'] ?? []), value_string]
            }
        }
    }
}

// Either delete one option from 'in' condition (if multiple options are selected),
// or delete the whole condition
const deleteFilter = (
    state: PLPState,
    value_string: string,
    requestVar: string
): FilterListType => {
    let filteredCondition: Maybe<Array<Maybe<Scalars['String']>>> = []
    if (state.filters[requestVar]) {
        const inCondition = state.filters[requestVar]['in'] ?? []
        filteredCondition = inCondition.filter((value: any) => {
            return value !== value_string
        })
    }
    if (filteredCondition && filteredCondition.length > 0) {
        state.filters[requestVar]['in'] = filteredCondition
    } else {
        delete state.filters[requestVar]
    }
    return state.filters
}
